'use client';

import { c as _c } from "react/compiler-runtime";
import { type AdFormat, isAdFormat, isMatchingAdAttemptFormat, normalizeAdSdkLifecycleEvent } from '@/constants/ad-sdk';
import { BI_EVENT_AD_ERROR } from '@/constants/bi-sdk';
import { useAuthContext } from '@/context/AuthContext';
import { useBISdkContext } from '@/context/BISdkContext';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { isAdFreeWorld } from '@/lib/configs';
import { env } from '@/lib/runtime-env';
import { getLocationHrefUrl } from '@/lib/url';
import { usePostHog } from '@posthog/next';
import { useSearchParams } from 'next/navigation';
import { RefObject, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
type AdSdkAuthState = {
  sessionId: string;
  origin: string;
  sourceWindow: MessageEventSource;
  appId: string;
};
type BreakpointAdState = {
  attemptId: string;
  createdAt: number;
};
const AD_SDK_DEBUG = 'ad-sdk-debug' as const;
const MAX_BI_FIELD_LENGTH = 4_096;

// Cooldown duration for non-reward ads
const NON_REWARD_AD_COOLDOWN_MS = 30 * 1000; // 30 seconds
const NON_REWARD_AD_FORMATS: AdFormat[] = ['video', 'overlay'];
const BREAKPOINT_AD_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
const BREAKPOINT_AD_FORMATS: AdFormat[] = ['video', 'reward_video', 'overlay', 'reward_overlay'];

// Events that indicate a breakpoint ad has finished (either completed or interrupted)
const BREAK_FINISH_EVENTS = ['GPT_SLOT_CLOSED', 'GPT_AD_ERROR', 'IMA_CONTENT_RESUME_REQUESTED', 'IMA_AD_ERROR'];
export const VIVERSE_AD_SDK_METHODS = {
  HANDSHAKE: 'VIVERSE_AD_SDK/handshake',
  HANDSHAKE_ACK: 'VIVERSE_AD_SDK/handshake:ack',
  HANDSHAKE_REJECT: 'VIVERSE_AD_SDK/handshake:reject',
  ATTEMPT_CREATE: 'VIVERSE_AD_SDK/attempt:create',
  ATTEMPT_CREATE_ACK: 'VIVERSE_AD_SDK/attempt:create:ack',
  ATTEMPT_CREATE_REJECT: 'VIVERSE_AD_SDK/attempt:create:reject',
  EVENT: 'VIVERSE_AD_SDK/event',
  EVENT_REJECTED: 'VIVERSE_AD_SDK/event:rejected'
} as const;

// Platform-owned ad unit paths (Phase 1: bootstrap values — move to platform config in a later phase)
export const AD_UNIT_PATHS: Record<AdFormat, string> = {
  banner: env('NEXT_PUBLIC_VADK_BANNER_UNIT_PATH') || '',
  video: env('NEXT_PUBLIC_VADK_VIDEO_UNIT_PATH') || '',
  reward_video: env('NEXT_PUBLIC_VADK_REWARD_VIDEO_UNIT_PATH') || '',
  overlay: env('NEXT_PUBLIC_VADK_OVERLAY_UNIT_PATH') || '',
  reward_overlay: env('NEXT_PUBLIC_VADK_REWARD_OVERLAY_UNIT_PATH') || ''
};
type UseAdSDKConfig = {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  sceneSid?: string;
};
export function useAdSDK(t0) {
  const $ = _c(29);
  const {
    iframeRef,
    sceneSid
  } = t0;
  const {
    isGamePlus
  } = useAuthContext();
  const {
    roomData
  } = useCheckEnterRoomContext();
  const {
    sendServerBIReport
  } = useBISdkContext();
  const {
    accountId
  } = useConfigsContext();
  const posthog = usePostHog();
  const roomId = roomData?.id;
  const hubSid = roomData?.hub_sid;
  let t1;
  if ($[0] !== isGamePlus || $[1] !== roomData?.tags) {
    t1 = isAdFreeWorld(roomData?.tags ?? []) || isGamePlus;
    $[0] = isGamePlus;
    $[1] = roomData?.tags;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const sdkAdFree = t1;
  const searchParams = useSearchParams();
  let t2;
  if ($[3] !== searchParams) {
    t2 = searchParams.has(AD_SDK_DEBUG);
    $[3] = searchParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const debug = t2;
  const gamEnvironment = env("NEXT_PUBLIC_ENVIRONMENT") === "production" ? "production" : "staging";
  const adSdkAuthRef = useRef(null);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const adAttemptFormatsRef = useRef(t3);
  const nonRewardAdShownAtRef = useRef(null);
  const breakpointAdIdRef = useRef(null);
  let t4;
  if ($[6] !== debug) {
    t4 = (message, data) => {
      if (debug) {
        console.info("[Ad SDK]", message, data);
      }
    };
    $[6] = debug;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const logAdSdk = t4;
  const warnAdSdk = _temp;
  let t5;
  if ($[8] !== hubSid || $[9] !== iframeRef || $[10] !== logAdSdk || $[11] !== roomId || $[12] !== sceneSid) {
    t5 = (event, payload) => {
      const childAppId = typeof payload?.appId === "string" ? payload.appId : null;
      const childSessionId = typeof payload?.sessionId === "string" ? payload.sessionId : null;
      const reject = (code, reason) => {
        warnAdSdk("Handshake rejected", {
          code,
          reason
        });
        const source = event.source;
        source.postMessage({
          methods: VIVERSE_AD_SDK_METHODS.HANDSHAKE_REJECT,
          payload: {
            status: "REJECTED",
            code,
            reason,
            issuedAt: Date.now()
          }
        }, {
          targetOrigin: event.origin
        });
      };
      if (!childAppId || !childSessionId) {
        reject("MISSING_FIELDS", "appId and sessionId are required");
        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        reject("UNAUTHORIZED_SOURCE", "Handshake must come from the active child iframe");
        return;
      }
      if (!sceneSid) {
        reject("SCENE_NOT_READY", "Platform scene context is not available");
        return;
      }
      if (childAppId !== sceneSid) {
        reject("APP_ID_MISMATCH", "appId does not match the active scene");
        return;
      }
      adSdkAuthRef.current = {
        sessionId: childSessionId,
        origin: event.origin,
        sourceWindow: event.source,
        appId: childAppId
      };
      logAdSdk("Handshake authorized", {
        appId: childAppId,
        sessionId: childSessionId,
        origin: event.origin
      });
      const source_0 = event.source;
      source_0.postMessage({
        methods: VIVERSE_AD_SDK_METHODS.HANDSHAKE_ACK,
        payload: {
          status: "OK",
          roomId: roomId ?? "",
          hubSid: hubSid ?? "",
          sceneSid,
          sessionId: childSessionId,
          issuedAt: Date.now(),
          protocolVersion: "1",
          adUnitPaths: AD_UNIT_PATHS
        }
      }, {
        targetOrigin: event.origin
      });
    };
    $[8] = hubSid;
    $[9] = iframeRef;
    $[10] = logAdSdk;
    $[11] = roomId;
    $[12] = sceneSid;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleAdSdkHandshake = t5;
  let t6;
  if ($[14] !== accountId || $[15] !== hubSid || $[16] !== logAdSdk || $[17] !== posthog || $[18] !== roomData?.title || $[19] !== sdkAdFree || $[20] !== sendServerBIReport) {
    t6 = (event_0, payload_0) => {
      const auth = adSdkAuthRef.current;
      const childSessionId_0 = typeof payload_0?.sessionId === "string" ? payload_0.sessionId : null;
      const childAppId_0 = typeof payload_0?.appId === "string" ? payload_0.appId : null;
      const traceId = typeof payload_0?.traceId === "string" ? payload_0.traceId : "unknown";
      const eventType = typeof payload_0?.eventType === "string" ? payload_0.eventType : "UNKNOWN";
      const adAttemptId = typeof payload_0?.adAttemptId === "string" ? payload_0.adAttemptId : null;
      const adFormat = isAdFormat(payload_0?.adFormat) ? payload_0.adFormat : null;
      const errorCode = typeof payload_0?.errorCode === "string" ? payload_0.errorCode : null;
      const errorMessage = typeof payload_0?.errorMessage === "string" ? payload_0.errorMessage : null;
      const reject_0 = (code_0, reason_0) => {
        warnAdSdk("Event rejected", {
          code: code_0,
          reason: reason_0,
          eventType,
          traceId
        });
        const source_1 = event_0.source;
        source_1.postMessage({
          methods: VIVERSE_AD_SDK_METHODS.EVENT_REJECTED,
          payload: {
            code: code_0,
            reason: reason_0,
            traceId,
            issuedAt: Date.now()
          }
        }, {
          targetOrigin: event_0.origin
        });
      };
      if (!auth) {
        reject_0("NOT_AUTHORIZED", "No active handshake session");
        return;
      }
      if (event_0.source !== auth.sourceWindow) {
        reject_0("UNAUTHORIZED_SOURCE", "Event source does not match authorized child window");
        return;
      }
      if (event_0.origin !== auth.origin) {
        reject_0("UNAUTHORIZED_ORIGIN", "Event origin does not match authorized child origin");
        return;
      }
      if (childSessionId_0 !== auth.sessionId) {
        reject_0("SESSION_MISMATCH", "Session ID does not match active authorized session");
        return;
      }
      if (childAppId_0 && childAppId_0 !== auth.appId) {
        reject_0("APP_ID_MISMATCH", "appId does not match the active authorized session");
        return;
      }
      if (!payload_0) {
        reject_0("MISSING_PAYLOAD", "Event payload is required");
        return;
      }
      if (!adAttemptId || !adFormat) {
        reject_0("INVALID_AD_EVENT", "adAttemptId and adFormat are required");
        return;
      }
      const issuedAdFormat = adAttemptFormatsRef.current.get(adAttemptId);
      if (!issuedAdFormat) {
        reject_0("UNKNOWN_AD_ATTEMPT_ID", "adAttemptId does not match any registered attempts");
        return;
      }
      if (!isMatchingAdAttemptFormat(issuedAdFormat, adFormat)) {
        reject_0("AD_FORMAT_MISMATCH", "adFormat does not match the registered attempt");
        return;
      }
      if (breakpointAdIdRef.current && adAttemptId === breakpointAdIdRef.current.attemptId) {
        if (BREAK_FINISH_EVENTS.includes(eventType)) {
          breakpointAdIdRef.current = null;
        }
      }
      if (sdkAdFree) {
        logAdSdk("Ignored event for ad-free world", {
          eventType,
          traceId,
          adAttemptId,
          adFormat: issuedAdFormat
        });
        return;
      }
      const normalizedEvent = normalizeAdSdkLifecycleEvent({
        adFormat: issuedAdFormat,
        eventType,
        errorCode
      });
      logAdSdk("Accepted event", {
        eventType,
        provider: payload_0.provider,
        traceId,
        sessionId: childSessionId_0,
        adAttemptId,
        adFormat: issuedAdFormat,
        ...(payload_0.data !== undefined && {
          data: payload_0.data
        })
      });
      if (!normalizedEvent) {
        return;
      }
      const errorFields = normalizedEvent === BI_EVENT_AD_ERROR ? {
        a6: errorCode ?? "",
        a7: errorMessage?.slice(0, MAX_BI_FIELD_LENGTH) ?? ""
      } : {};
      sendServerBIReport({
        ev: normalizedEvent,
        category: "sdk_ads_lifecycle",
        account_id: accountId,
        a1: getLocationHrefUrl(),
        a2: roomData?.title ?? "",
        a3: hubSid ?? "",
        a4: adAttemptId,
        a5: issuedAdFormat,
        ...errorFields,
        a13: posthog.get_distinct_id()
      });
    };
    $[14] = accountId;
    $[15] = hubSid;
    $[16] = logAdSdk;
    $[17] = posthog;
    $[18] = roomData?.title;
    $[19] = sdkAdFree;
    $[20] = sendServerBIReport;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleAdSdkEvent = t6;
  let t7;
  if ($[22] !== logAdSdk || $[23] !== sdkAdFree) {
    t7 = (event_1, payload_1) => {
      const auth_0 = adSdkAuthRef.current;
      const childSessionId_1 = typeof payload_1?.sessionId === "string" ? payload_1.sessionId : null;
      const childAppId_1 = typeof payload_1?.appId === "string" ? payload_1.appId : null;
      const traceId_0 = typeof payload_1?.traceId === "string" ? payload_1.traceId : "unknown";
      const adFormat_0 = typeof payload_1?.adFormat === "string" ? payload_1.adFormat as AdFormat : null;
      const requestedAdUnitPath = typeof payload_1?.adUnitPath === "string" ? payload_1.adUnitPath : undefined;
      const reject_1 = (code_1, reason_1) => {
        warnAdSdk("Attempt create rejected", {
          code: code_1,
          reason: reason_1,
          adFormat: adFormat_0,
          traceId: traceId_0
        });
        const source_2 = event_1.source;
        source_2.postMessage({
          methods: VIVERSE_AD_SDK_METHODS.ATTEMPT_CREATE_REJECT,
          payload: {
            code: code_1,
            reason: reason_1,
            traceId: traceId_0,
            issuedAt: Date.now()
          }
        }, {
          targetOrigin: event_1.origin
        });
      };
      if (!auth_0) {
        reject_1("NOT_AUTHORIZED", "No active handshake session");
        return;
      }
      if (event_1.source !== auth_0.sourceWindow) {
        reject_1("UNAUTHORIZED_SOURCE", "Attempt source does not match authorized child window");
        return;
      }
      if (event_1.origin !== auth_0.origin) {
        reject_1("UNAUTHORIZED_ORIGIN", "Attempt origin does not match authorized child origin");
        return;
      }
      if (childSessionId_1 !== auth_0.sessionId) {
        reject_1("SESSION_MISMATCH", "Session ID does not match active authorized session");
        return;
      }
      if (childAppId_1 && childAppId_1 !== auth_0.appId) {
        reject_1("APP_ID_MISMATCH", "appId does not match the active authorized session");
        return;
      }
      if (!adFormat_0 || !isAdFormat(adFormat_0)) {
        reject_1("INVALID_AD_FORMAT", "adFormat is missing or not supported by the platform");
        return;
      }
      const platformAdUnitPath = AD_UNIT_PATHS[adFormat_0 as AdFormat];
      if (requestedAdUnitPath !== platformAdUnitPath) {
        reject_1("AD_UNIT_PATH_MISMATCH", "Requested adUnitPath does not match configuration");
        return;
      }
      if (BREAKPOINT_AD_FORMATS.includes(adFormat_0) && breakpointAdIdRef.current !== null) {
        const now = Date.now();
        const createdAt = breakpointAdIdRef.current.createdAt;
        const timeout = now > createdAt + BREAKPOINT_AD_TIMEOUT_MS;
        if (!timeout) {
          reject_1("ACTIVE_BREAKPOINT_AD", "Another ad is active. Please wait until it finishes.");
          return;
        }
      }
      if (NON_REWARD_AD_FORMATS.includes(adFormat_0) && nonRewardAdShownAtRef.current !== null) {
        const now_0 = Date.now();
        const lastShown = nonRewardAdShownAtRef.current;
        if (now_0 < lastShown + NON_REWARD_AD_COOLDOWN_MS) {
          const cooldownSeconds = NON_REWARD_AD_COOLDOWN_MS / 1000;
          reject_1("COOLDOWN_ACTIVE", `Ad is cooling down for ${cooldownSeconds} seconds. Please avoid frequent requests.`);
          return;
        }
      }
      const adAttemptId_0 = uuidv4();
      adAttemptFormatsRef.current.set(adAttemptId_0, adFormat_0);
      if (NON_REWARD_AD_FORMATS.includes(adFormat_0) && !sdkAdFree) {
        nonRewardAdShownAtRef.current = Date.now();
      }
      if (BREAKPOINT_AD_FORMATS.includes(adFormat_0) && !sdkAdFree) {
        breakpointAdIdRef.current = {
          attemptId: adAttemptId_0,
          createdAt: Date.now()
        };
      }
      logAdSdk("Accepted attempt:create", {
        adAttemptId: adAttemptId_0,
        adFormat: adFormat_0,
        traceId: traceId_0,
        sessionId: childSessionId_1,
        platformAdUnitPath
      });
      const source_3 = event_1.source;
      source_3.postMessage({
        methods: VIVERSE_AD_SDK_METHODS.ATTEMPT_CREATE_ACK,
        payload: {
          adAttemptId: adAttemptId_0,
          traceId: traceId_0,
          issuedAt: Date.now(),
          targeting: {
            environment: gamEnvironment,
            ad_free: sdkAdFree,
            content_rating: "general"
          }
        }
      }, {
        targetOrigin: event_1.origin
      });
    };
    $[22] = logAdSdk;
    $[23] = sdkAdFree;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const handleAdSdkAttemptCreate = t7;
  let t8;
  if ($[25] !== handleAdSdkAttemptCreate || $[26] !== handleAdSdkEvent || $[27] !== handleAdSdkHandshake) {
    const handleAdSdkMessage = (event_2, methods, payload_2) => {
      bb327: switch (methods) {
        case VIVERSE_AD_SDK_METHODS.HANDSHAKE:
          {
            handleAdSdkHandshake(event_2, payload_2);
            break bb327;
          }
        case VIVERSE_AD_SDK_METHODS.ATTEMPT_CREATE:
          {
            handleAdSdkAttemptCreate(event_2, payload_2);
            break bb327;
          }
        case VIVERSE_AD_SDK_METHODS.EVENT:
          {
            handleAdSdkEvent(event_2, payload_2);
          }
      }
    };
    t8 = {
      handleAdSdkMessage
    };
    $[25] = handleAdSdkAttemptCreate;
    $[26] = handleAdSdkEvent;
    $[27] = handleAdSdkHandshake;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}
function _temp(message_0, data_0) {
  console.warn("[Ad SDK]", message_0, data_0);
}